import { FormEvent, useMemo, useState } from "react";
import { DefaultButton, MessageBar, MessageBarType, PrimaryButton, TextField } from "@fluentui/react";

import styles from "./AdminDevConsole.module.css";
import { getToken, useLogin } from "../../authConfig";
import { inviteUserApi } from "../../api";
import { useAuthClient } from "../../useAuthClient";
import { usePersistedInvites } from "../../hooks/usePersistedInvites";

type StatusMessage = {
    type: "success" | "error";
    message: string;
};

export const AdminDevConsole = () => {
    const auth = useAuthClient();
    const [inviteName, setInviteName] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const { invites: recentInvites, addInvite, clearInvites, isLoaded } = usePersistedInvites();
    const [status, setStatus] = useState<StatusMessage | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const emailIsValid = useMemo(() => {
        if (!inviteEmail) {
            return false;
        }
        const trimmed = inviteEmail.trim();
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    }, [inviteEmail]);

    const nameIsValid = inviteName.trim().length > 1;
    const formIsValid = emailIsValid && nameIsValid;

    const resetForm = () => {
        setInviteName("");
        setInviteEmail("");
    };

    const handleAddInvitee = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!formIsValid || isSubmitting) {
            if (!formIsValid) {
                setStatus({ type: "error", message: "Please provide a name and a valid email address." });
            }
            return;
        }

        const trimmedName = inviteName.trim();
        const trimmedEmail = inviteEmail.trim().toLowerCase();

        try {
            setIsSubmitting(true);
            setStatus(null);

            if (useLogin && !auth) {
                throw new Error("Authentication is required to manage invitations.");
            }

            const token = useLogin ? await getToken(auth) : undefined;
            const response = await inviteUserApi({ name: trimmedName, email: trimmedEmail }, token);
            const createdAt = response.created_at ? new Date(response.created_at) : new Date();
            const addedAt = Number.isNaN(createdAt.getTime()) ? new Date() : createdAt;

            const inviteeName = response.name || trimmedName;
            addInvite({
                id: response.id || trimmedEmail,
                name: inviteeName,
                email: response.email || trimmedEmail,
                addedAt,
                flow: response.flow,
                status: response.status,
                message: response.message
            });
            setStatus({ type: "success", message: response.message || `${inviteeName} has been invited.` });
            resetForm();
        } catch (error) {
            const err = error as Error & { details?: unknown };
            const details = err.details as Record<string, unknown> | string | undefined;
            const detailMessage =
                typeof details === "string"
                    ? details
                    : typeof details === "object" && details !== null && typeof details["message"] === "string"
                    ? (details["message"] as string)
                    : undefined;
            setStatus({ type: "error", message: detailMessage || err.message || "Failed to add invite." });
            console.error("[DevConsole] Failed to add invite", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <p className={styles.kicker}>Admin</p>
                    <h1 className={styles.title}>Admin Console</h1>
                    <p className={styles.subtitle}>
                        Invite Harbor teammates or external users directly from the app.
                    </p>
                </div>
            </header>

            <section className={styles.card}>
                <form className={styles.form} onSubmit={handleAddInvitee}>
                    <div className={styles.inlineFields}>
                        <TextField label="Full name" value={inviteName} onChange={(_, v) => setInviteName(v ?? "")} required />
                        <TextField label="Work email" value={inviteEmail} onChange={(_, v) => setInviteEmail(v ?? "")} type="email" required />
                    </div>
                    <PrimaryButton type="submit" text={isSubmitting ? "Sending..." : "Add person"} disabled={!formIsValid || isSubmitting} />
                </form>
                {status && (
                    <MessageBar messageBarType={status.type === "success" ? MessageBarType.success : MessageBarType.error} onDismiss={() => setStatus(null)}>
                        {status.message}
                    </MessageBar>
                )}
            </section>

            <section className={styles.activitySection}>
                <div className={styles.sectionHeading}>
                    <div className={styles.headingRow}>
                        <h2>Invite History</h2>
                        {recentInvites.length > 0 && (
                            <DefaultButton text="Clear history" onClick={clearInvites} className={styles.clearButton} />
                        )}
                    </div>
                    <p>Recent invitations are saved locally and will persist across sessions.</p>
                </div>
                {!isLoaded ? (
                    <div className={styles.emptyState}>Loading...</div>
                ) : recentInvites.length === 0 ? (
                    <div className={styles.emptyState}>No invites yet. Add someone above to get started.</div>
                ) : (
                    <div className={styles.inviteList}>
                        {recentInvites.map(invite => (
                            <div key={invite.id} className={styles.inviteCard}>
                                <div className={styles.inviteMeta}>
                                    <span className={styles.flowBadge}>{invite.flow === "internal" ? "Harbor" : "External"}</span>
                                    <span className={styles.statusText}>{invite.status}</span>
                                </div>
                                <div className={styles.inviteTitle}>{invite.name}</div>
                                <div>{invite.email}</div>
                                {invite.message && <div className={styles.subduedText}>{invite.message}</div>}
                                <div className={styles.subduedText}>{new Date(invite.addedAt).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default AdminDevConsole;
